import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import trackServiceInquiry from '../../utils/trackServiceInquiry';

// Reuse the exact contact-page email capture.
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/meewwwbb';
const SESSION_KEY = 'db_mobile_demo_seen';
const MOBILE_MAX = 768; // px
const FIRE_DELAY = 3000; // ms, or first scroll, whichever comes first

const PLATFORMS = [
  'AgentForce',
  'Microsoft Copilot',
  'LangChain',
  'OpenAI Enterprise',
  'Palantir',
  'Others',
];

const REASSURANCES = [
  'Skip the pitch - just an honest tour.',
  'Watch it work on real data.',
  'Tailored to your exact case.',
  'Live answers for your questions.',
];

const css = `
  .mld-root{
    --blue:#0072F5; --blue-dark:#0052B3; --ink:#0B0D10;
    --paper:#F6F4EF; --paper-dim:#ECE8DF; --line:rgba(11,13,16,0.12);
    --muted:#52555a;
    font-family:'Inter',sans-serif; -webkit-font-smoothing:antialiased;
  }
  .mld-root *{box-sizing:border-box;}
  .mld-root .serif{font-family:'Newsreader',serif;font-weight:400;}

  .mld-overlay{
    position:fixed; inset:0; z-index:9999;
    background:var(--paper); color:var(--ink);
    overflow-y:auto; -webkit-overflow-scrolling:touch;
    animation:mld-fade .28s ease both;
  }
  @keyframes mld-fade{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:none;}}
  @media (prefers-reduced-motion:reduce){.mld-overlay{animation:none;}}

  .mld-bar{
    position:sticky; top:0; z-index:2;
    display:flex; align-items:center; justify-content:space-between;
    padding:16px 20px; background:var(--ink);
    border-bottom:1px solid rgba(255,255,255,0.08);
  }
  .mld-bar .logo{font-weight:800;font-size:17px;letter-spacing:-0.02em;color:#fff;}
  .mld-bar .logo span{color:#8fbaff;}
  .mld-close{
    background:none;border:none;cursor:pointer;padding:8px;margin:-8px;
    color:#fff;line-height:0;
  }

  .mld-wrap{padding:28px 20px 0;}
  .mld-card{
    background:#fff;border:1px solid var(--line);border-radius:14px;
    padding:28px 20px; box-shadow:0 1px 3px rgba(0,0,0,0.05);
  }

  .mld-head{margin:0 0 4px;}
  .mld-head h1{
    font-size:38px;line-height:1.02;letter-spacing:-0.01em;margin:0;color:var(--ink);
  }
  .mld-head h1 em{font-style:italic;display:block;}
  .mld-rule{height:1px;background:var(--line);margin:24px 0 8px;}

  .mld-field{margin-bottom:20px;}
  .mld-field label{display:block;font-size:15px;font-weight:600;margin-bottom:8px;color:var(--ink);}
  .mld-field .req{color:var(--blue);}
  .mld-field input,
  .mld-field textarea{
    width:100%;font-family:'Inter',sans-serif;font-size:16px;padding:14px 15px;
    border:1px solid var(--line);border-radius:10px;background:var(--paper);
    color:var(--ink);transition:border-color .2s;
  }
  .mld-field input::placeholder,
  .mld-field textarea::placeholder{color:#9a9da2;}
  .mld-field input:focus,
  .mld-field textarea:focus{outline:none;border-color:var(--blue);background:#fff;}

  .mld-check-group{margin:22px 0 4px;}
  .mld-check-group > .q{font-size:15px;font-weight:600;margin-bottom:14px;color:var(--ink);}
  .mld-check{
    display:flex;align-items:center;gap:14px;padding:9px 0;cursor:pointer;
    font-size:16px;color:var(--ink);user-select:none;
  }
  .mld-check input{
    appearance:none;-webkit-appearance:none;flex:0 0 auto;
    width:24px;height:24px;border:1.5px solid var(--line);border-radius:7px;
    background:var(--paper);cursor:pointer;position:relative;transition:all .15s;
  }
  .mld-check input:checked{background:var(--blue);border-color:var(--blue);}
  .mld-check input:checked::after{
    content:'';position:absolute;left:8px;top:4px;width:6px;height:11px;
    border:solid #fff;border-width:0 2px 2px 0;transform:rotate(45deg);
  }
  .mld-check input:focus-visible{outline:2px solid var(--blue);outline-offset:2px;}

  .mld-submit-row{display:flex;justify-content:center;margin:30px 0 6px;}
  .mld-submit{
    display:inline-flex;align-items:center;gap:10px;
    background:var(--blue);color:#fff;border:none;cursor:pointer;
    font-family:'Inter',sans-serif;font-size:16px;font-weight:700;letter-spacing:.06em;
    padding:16px 44px;border-radius:9999px;transition:background .2s,opacity .2s;
  }
  .mld-submit:hover:not(:disabled){background:var(--blue-dark);}
  .mld-submit:disabled{opacity:.55;cursor:not-allowed;}

  .mld-err{color:#c0392b;font-size:14px;margin:14px 0 0;text-align:center;}

  .mld-reassure{list-style:none;margin:34px auto 8px;padding:0;max-width:420px;}
  .mld-reassure li{
    display:flex;align-items:center;gap:14px;padding:11px 0;
    font-size:16px;color:var(--ink);
  }
  .mld-reassure svg{flex:0 0 auto;}

  .mld-success{text-align:center;padding:26px 6px 10px;}
  .mld-success .icon{
    width:60px;height:60px;border-radius:50%;background:var(--blue);color:#fff;
    display:flex;align-items:center;justify-content:center;margin:0 auto 22px;font-size:30px;line-height:1;
  }
  .mld-success h2{font-size:24px;font-weight:800;letter-spacing:-0.01em;margin:0 0 12px;}
  .mld-success p{font-size:15.5px;color:var(--muted);line-height:1.6;margin:0;}

  /* contact-style paper footer */
  .mld-footer{padding:40px 20px 40px;margin-top:36px;border-top:1px solid var(--line);}
  .mld-footer .logo{font-weight:800;font-size:18px;letter-spacing:-0.02em;color:var(--ink);}
  .mld-footer .logo span{color:var(--blue);}
  .mld-footer .blurb{font-size:14px;color:#6b6e73;margin:12px 0 28px;line-height:1.55;max-width:300px;}
  .mld-footer .cols{display:grid;grid-template-columns:1fr 1fr;gap:28px 20px;padding-bottom:26px;border-bottom:1px solid var(--line);}
  .mld-footer .col-title{font-size:12px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#6b6e73;margin-bottom:14px;}
  .mld-footer .cols a{display:block;font-size:14px;color:#52555a;padding:6px 0;text-decoration:none;}
  .mld-footer .cols a:hover{color:var(--ink);}
  .mld-footer .bottom{font-size:13px;color:#6b6e73;padding-top:22px;}
`;

const CheckCircle = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10.25" stroke="#0072F5" strokeWidth="1.5" />
    <path d="M7.5 12.4l3 3 6-6.4" stroke="#0072F5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MobileLeadCapture = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', problem: '' });
  const [platforms, setPlatforms] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [platformError, setPlatformError] = useState(false);

  // Eligibility: home page + mobile + not already seen this session.
  useEffect(() => {
    if (pathname !== '/') return undefined;

    let seen = null;
    try { seen = sessionStorage.getItem(SESSION_KEY); } catch { /* private mode */ }
    if (seen) return undefined;

    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia(`(max-width:${MOBILE_MAX}px)`).matches;
    if (!isMobile) return undefined;

    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
      setOpen(true);
      cleanup();
    };
    const timer = setTimeout(fire, FIRE_DELAY);
    const onScroll = () => fire();
    window.addEventListener('scroll', onScroll, { passive: true });

    function cleanup() {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    }
    return cleanup;
  }, [pathname]);

  // Lock background scroll + allow Esc to close while open.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = () => setOpen(false);
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const togglePlatform = (p) => {
    setPlatformError(false);
    setPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (platforms.length === 0) { setPlatformError(true); return; }
    setStatus('submitting');
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.problem,
          platforms: platforms.join(', '),
          formType: 'mobile-demo',
          _subject: 'New mobile demo request (DataBackfill)',
        }),
      });
      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) trackServiceInquiry('mobile_demo');
    } catch {
      setStatus('error');
    }
  };

  if (!open) return null;

  return (
    <div className="mld-root">
      <style>{css}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
      <link
        href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;1,6..72,400&family=Inter:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      <div className="mld-overlay" role="dialog" aria-modal="true" aria-label="Book a demo">
        <div className="mld-bar">
          <span className="logo">Data<span>Backfill</span></span>
          <button className="mld-close" aria-label="Close" onClick={close}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="mld-wrap">
          <div className="mld-card">
            {status === 'success' ? (
              <div className="mld-success">
                <div className="icon">✓</div>
                <h2>Thanks! We'll be in touch.</h2>
                <p>We got your request and will reach out shortly to set up your walkthrough.</p>
              </div>
            ) : (
              <>
                <div className="mld-head">
                  <h1 className="serif">Let us give you<em>the grand tour.</em></h1>
                </div>
                <div className="mld-rule" />

                <form onSubmit={handleSubmit} noValidate>
                  <div className="mld-field">
                    <label htmlFor="mld-name">Name <span className="req">*</span></label>
                    <input id="mld-name" name="name" type="text" placeholder="eg: Grace Hopper"
                      value={form.name} onChange={handleChange} required />
                  </div>
                  <div className="mld-field">
                    <label htmlFor="mld-email">Work email <span className="req">*</span></label>
                    <input id="mld-email" name="email" type="email" placeholder="eg: grace@databackfill.com"
                      value={form.email} onChange={handleChange} required />
                  </div>
                  <div className="mld-field">
                    <label htmlFor="mld-phone">Phone Number</label>
                    <input id="mld-phone" name="phone" type="tel" placeholder="eg: +1 949 867 5309"
                      value={form.phone} onChange={handleChange} />
                  </div>
                  <div className="mld-field">
                    <label htmlFor="mld-problem">What problem are you trying to solve with AI agents? <span className="req">*</span></label>
                    <textarea id="mld-problem" name="problem" rows={3}
                      placeholder="eg: I want to build AI agents, automate workflows, or just explore what's possible."
                      value={form.problem} onChange={handleChange} required />
                  </div>

                  <div className="mld-check-group">
                    <div className="q">Which platforms or tools are you currently using or evaluating? <span className="req">*</span></div>
                    {PLATFORMS.map((p) => (
                      <label className="mld-check" key={p}>
                        <input type="checkbox" checked={platforms.includes(p)} onChange={() => togglePlatform(p)} />
                        <span>{p}</span>
                      </label>
                    ))}
                    {platformError && <p className="mld-err" style={{ textAlign: 'left' }}>Please select at least one option.</p>}
                  </div>

                  <div className="mld-submit-row">
                    <button type="submit" className="mld-submit" disabled={status === 'submitting'}>
                      {status === 'submitting' ? 'SENDING…' : 'SUBMIT'}
                      {status !== 'submitting' && (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M7 17L17 7M17 7H8M17 7v9" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {status === 'error' && (
                    <p className="mld-err">
                      Something went wrong. Please try again or email{' '}
                      <a href="mailto:support@databackfill.com" style={{ color: '#c0392b', fontWeight: 600 }}>support@databackfill.com</a>.
                    </p>
                  )}
                </form>

                <ul className="mld-reassure">
                  {REASSURANCES.map((r) => (
                    <li key={r}><CheckCircle /><span>{r}</span></li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <footer className="mld-footer">
            <div className="logo">Data<span>Backfill</span></div>
            <p className="blurb">A boutique AI &amp; data delivery studio: senior engineers, architects, and product designers shipping production systems.</p>
            <div className="cols">
              <div>
                <div className="col-title">Services</div>
                <Link to="/#services" onClick={close}>Claude Implementation</Link>
                <Link to="/#services" onClick={close}>RAG &amp; Knowledge Systems</Link>
                <Link to="/#services" onClick={close}>Agentic Workflows</Link>
                <Link to="/#services" onClick={close}>Data Infrastructure</Link>
              </div>
              <div>
                <div className="col-title">Company</div>
                <Link to="/#work" onClick={close}>Case Studies</Link>
                <Link to="/#team" onClick={close}>Team</Link>
                <Link to="/#pricing" onClick={close}>Pricing</Link>
                <Link to="/contact" onClick={close}>Contact</Link>
              </div>
            </div>
            <div className="bottom">© {new Date().getFullYear()} DataBackfill. All rights reserved.</div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default MobileLeadCapture;
